import { openPromptTemplateSelection, applyPromptTemplateSelection, type PromptTemplateSelection } from "../editing-dispatch.js";
import { createCanvasEditingDispatch } from "../editing-dispatch.js";
import { CanvasPromptEditor } from "../nodes/InlineTextEditor.js";
import { referenceSnapshotForDocument, type EditorReferenceSnapshot } from "../reference-snapshot.js";
import { serializePortablePrompt, type PromptContent } from "@seedance/contracts";
import { StructuredPromptEditor } from "../prompts/StructuredPromptEditor.js";
import { PromptBindingStatus } from "../prompts/PromptBindingStatus.js";
import { parseExternalPromptText, replacePromptRange, promptLength, promptOffsetToPoint, type PromptRange } from "../prompts/prompt-edit-operations.js";
import { readPromptSelection } from "../prompts/prompt-dom.js";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent
} from "react";
import type {
  AssetView,
  CanvasDocumentV3 as CanvasDocument,
  ReferenceMode,
  ReferenceRole,
  SeedanceCapabilitiesView,
  SeedanceNodeSettingsV3 as SeedanceNodeSettings
} from "@seedance/contracts";
import {
  getSeedanceModelCapability,
  normalizeSeedanceSettings,
  type SeedanceModelCapability
} from "@seedance/contracts";
import type { BuildPreviewResult } from "@seedance/workflow";
import {
  canvasCopy,
  referenceModeLabel,
  referenceRoleLabel,
  seedanceModelLabel
} from "../../copy/zh-CN.js";
import { assetThumbnailUrl } from "../nodes/node-types.js";
import { parseCapabilityNumber } from "../panels/capability-number.js";
import { buildEditorRequestPreview } from "../panels/RequestPreviewPanel.js";
import {
  buildEditorPromptReferences,
  removeCanvasEdge,
  updateReferenceMode,
  updateReferenceRole,
  updateSeedanceSettings
} from "../seedance-editor.js";
import type {
  AssetReferenceSearchState,
  ComposerCandidate
} from "./asset-reference-search.js";
import {
  acceptMentionSelection,
  dismissMentionSelection,
  moveMentionSelection,
  shouldHandleComposerPromptKey,
  type MentionListState
} from "./composer-interactions.js";
import { mentionQueryAtCaret, type MentionQuery } from "./mention-query.js";
import {
  PromptTemplatePicker,
  applyPromptTemplate,
  type PromptTemplateApplyMode
} from "./PromptTemplatePicker.js";
import { removeComposerReference } from "./reference-actions.js";

type SeedanceNode = Extract<CanvasDocument["nodes"][number], { kind: "seedance" }>;
type ReferenceKind = "image" | "video" | "audio";

export interface SeedanceComposerProps {
  document: CanvasDocument;
  referenceSnapshot?: EditorReferenceSnapshot;
  seedanceNode: SeedanceNode;
  capabilities: SeedanceCapabilitiesView;
  assets: readonly AssetView[];
  candidates: { canvas: ComposerCandidate[]; library: ComposerCandidate[] };
  candidateStatus: AssetReferenceSearchState["status"];
  candidateHasMore: boolean;
  onSearchCandidates(query: string): void;
  onLoadMoreCandidates(): void;
  onChange(document: CanvasDocument): void;
  onSelectCandidate(candidate: ComposerCandidate, query: PromptRange, prompt: PromptContent, sourceNodeId:string): void;
  onUndo?(): void;
  onRedo?(): void;
  onValidate(preview: BuildPreviewResult): void;
  onSubmit(preview: BuildPreviewResult): Promise<string>;
  onSavePromptTemplate(input: { title: string; content: string }): Promise<void> | void;
  onManagePromptTemplates(): void;
  onClose(): void;
}

const videoModes: readonly ReferenceMode[] = [
  "strict",
  "action_only",
  "camera_only",
  "rhythm_only",
  "style_only",
  "custom"
];

export function applyPromptTemplateToSeedance(
  document: CanvasDocument,
  seedanceNodeId: string,
  body: string
): CanvasDocument {
  return updateSeedanceSettings(document, seedanceNodeId, { prompt: parseExternalPromptText(body, () => crypto.randomUUID()) });
}

function rolesForKind(
  kind: ReferenceKind,
  modelCapability: SeedanceModelCapability
): readonly ReferenceRole[] {
  return modelCapability.allowedRoles[kind];
}

function flatCandidates(candidates: SeedanceComposerProps["candidates"]): ComposerCandidate[] {
  return [...candidates.canvas, ...candidates.library];
}

function initialMention(content:PromptContent, nodeId:string) {
  const segmentIndex = content.segments.length - 1;
  const last = content.segments[segmentIndex];
  if (last?.type !== "text") return undefined;
  const query = mentionQueryAtCaret(last.text,last.text.length);
  return query ? {nodeId,range:{start:{segmentIndex,offset:query.start},end:{segmentIndex,offset:query.end}},query:query.query,content:structuredClone(content)} : undefined;
}

export function SeedanceComposer({
  document,
  referenceSnapshot,
  seedanceNode,
  capabilities,
  assets,
  candidates,
  candidateStatus,
  candidateHasMore,
  onSearchCandidates,
  onLoadMoreCandidates,
  onChange,
  onSelectCandidate,
  onValidate,
  onSubmit,
  onSavePromptTemplate,
  onManagePromptTemplates,
  onClose, onUndo, onRedo
}: SeedanceComposerProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const prompt = seedanceNode.settings.prompt;
  const [query, setQuery] = useState(() => initialMention(prompt,seedanceNode.id));
  const [templateSelection, setTemplateSelection] = useState<PromptTemplateSelection>();
  const [mentionList, setMentionList] = useState<MentionListState>({open:Boolean(query),activeIndex:0});
  const [validationStatus, setValidationStatus] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateSaveOpen, setTemplateSaveOpen] = useState(false);
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateStatus, setTemplateStatus] = useState<string>();
  const options = useMemo(() => flatCandidates(candidates), [candidates]);
  const snapshot = useMemo(() => referenceSnapshotForDocument(document, referenceSnapshot), [document, referenceSnapshot]);
  const resolved = useMemo(() => snapshot.resolve(seedanceNode.id), [snapshot, seedanceNode.id]);
  const references = resolved.references;
  const promptReferences = useMemo(
    () => buildEditorPromptReferences(document, seedanceNode.id),
    [document, seedanceNode.id]
  );
  const effectivePrompt = resolved.finalPrompt;
  const templateContent = serializePortablePrompt(prompt);
  const assetsById = useMemo(() => new Map(assets.map((asset) => [asset.id, asset])), [assets]);
  const modelCapability = useMemo(
    () => getSeedanceModelCapability(capabilities, seedanceNode.settings.model) ?? capabilities.models[0],
    [capabilities, seedanceNode.settings.model]
  );

  const previousNodeId = useRef(seedanceNode.id);
  useEffect(() => {
    if (previousNodeId.current === seedanceNode.id) return;
    previousNodeId.current = seedanceNode.id;
    setQuery(undefined); setMentionList(dismissMentionSelection());
  }, [seedanceNode.id]);

  useEffect(() => {
    if (query === undefined) {
      setMentionList(dismissMentionSelection());
      return;
    }
    setMentionList({ open: true, activeIndex: 0 });
    onSearchCandidates(query.query);
  }, [onSearchCandidates, query?.query]);

  const changeSettings = (patch: Partial<SeedanceNodeSettings>) =>
    onChange(updateSeedanceSettings(document, seedanceNode.id, patch));

  const changeModel = (model: SeedanceNodeSettings["model"]) => {
    const normalized = normalizeSeedanceSettings(model, seedanceNode.settings, capabilities);
    onChange(updateSeedanceSettings(document, seedanceNode.id, normalized.settings));
    setValidationStatus(
      normalized.repaired.length > 0
        ? normalized.repaired.map((repair) => repair.message).join(" ")
        : undefined
    );
  };

  const applyTemplate = (content: string, mode: PromptTemplateApplyMode) => {
    try {
      if (!templateSelection) throw new Error("请重新打开模板选择器。");
      onChange(applyPromptTemplateSelection(document,{nodeId:seedanceNode.id,field:"prompt",inputEdgeId:null},templateSelection,content,mode));
      setTemplatePickerOpen(false);
    } catch { setTemplateStatus("提示词已更新或模板无法安全插入，原内容已保留，请重新选择。"); }
  };

  const saveTemplate = async () => {
    const title = templateTitle.trim();
    if (!title || !templateContent.trim()) return;
    try {
      await onSavePromptTemplate({ title, content: templateContent });
      setTemplateStatus(canvasCopy.promptTemplateSaved);
      setTemplateTitle("");
      setTemplateSaveOpen(false);
    } catch {
      setTemplateStatus(canvasCopy.promptTemplateSaveFailed);
    }
  };

  const selectCandidate = (candidate: ComposerCandidate) => {
    if (query === undefined) return;
    try {
      onSelectCandidate(candidate, query.range, query.content, query.nodeId);
      setMentionList(dismissMentionSelection()); setQuery(undefined);
    } catch(error) { setValidationStatus(error instanceof Error ? error.message : "引用已变化，请重新选择。"); }
  };

  const handlePromptKeys = (event: KeyboardEvent<HTMLElement>) => {
    const ownEditable = editorRef.current?.querySelector<HTMLElement>("[contenteditable]");
    if (!shouldHandleComposerPromptKey(event.nativeEvent, Boolean(ownEditable?.contains(event.target as Node)))) return;
    if (!mentionList.open || query === undefined) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault(); event.stopPropagation();
      setMentionList((current) => moveMentionSelection(current, event.key === "ArrowDown" ? 1 : -1, options.length));
      return;
    }
    if (event.key === "Enter") {
      const candidate = acceptMentionSelection(options, mentionList.activeIndex);
      if (!candidate) return;
      event.preventDefault(); event.stopPropagation();
      selectCandidate(candidate);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setMentionList(dismissMentionSelection());
      setQuery(undefined);
      editorRef.current?.querySelector<HTMLElement>("[contenteditable]")?.focus();
    }
  };

  const changeDuration = (value: string) => {
    if (!modelCapability) return;
    const durationSeconds = parseCapabilityNumber(value, modelCapability.durationSeconds);
    if (durationSeconds !== undefined) changeSettings({ durationSeconds });
  };
  const changeSeed = (value: string) => {
    const seed = parseCapabilityNumber(value, capabilities.seed);
    if (seed !== undefined) changeSettings({ seed });
  };

  const submitGeneration = async () => {
    const preview = buildEditorRequestPreview({ document, seedanceNode, capabilities, assets });
    onValidate(preview);
    if (!preview.ready) {
      setValidationStatus("配置存在需要处理的引用或参数，请修正后重试。");
      return;
    }
    setSubmitting(true);
    setValidationStatus("正在提交生成任务…");
    try {
      setValidationStatus(await onSubmit(preview));
    } catch (error) {
      setValidationStatus(error instanceof Error ? error.message : "生成任务提交失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="studio-seedance-composer nodrag nowheel" aria-label="Seedance 生成配置" onKeyDownCapture={handlePromptKeys}>
      <header className="studio-seedance-composer__header">
        <span className="studio-seedance-composer__drag" data-composer-drag-handle aria-label="拖动 Seedance 生成卡片">⋮⋮</span>
        <strong>{seedanceModelLabel(seedanceNode.settings.model)}</strong>
        <button type="button" aria-label="关闭 Seedance 生成卡片" onClick={onClose}>×</button>
      </header>
      <div className="studio-seedance-composer__tags" aria-label="引用素材">
        {promptReferences.length === 0 && references.length === 0 ? <span className="studio-seedance-composer__empty">输入 @ 添加当前画布或私有素材</span> : null}
        {promptReferences.map((reference) => (
          <div className="studio-seedance-composer__tag" key={reference.edgeId}>
            <strong>{reference.label}</strong>
            <span title={reference.text}>{reference.text}</span>
            <details>
              <summary>编辑此生成节点的提示词引用</summary>
              {(() => {
                const sourceNode=document.nodes.find(node=>node.id===reference.sourceNodeId);
                const fields=sourceNode?.kind === "text" ? ["text" as const] : sourceNode?.kind === "script" ? ["title" as const,"synopsis" as const] : [];
                return fields.map(field=><CanvasPromptEditor key={field} document={document} referenceSnapshot={snapshot} source={{nodeId:reference.sourceNodeId,field,inputEdgeId:reference.edgeId}} targetNodeId={seedanceNode.id} onChange={onChange} ariaLabel={`${reference.label} ${field === "title" ? "标题" : field === "synopsis" ? "梗概" : "正文"}`} maxLength={field === "title" ? 160 : field === "text" ? 50_000 : 20_000} {...(onUndo ? {onUndo} : {})} {...(onRedo ? {onRedo} : {})}/>);
              })()}
            </details>
            <button
              type="button"
              aria-label={`移除 ${reference.label}`}
              onClick={() => createCanvasEditingDispatch({getDocument:()=>document,change:onChange}).deleteEdges([reference.edgeId])}
            >×</button>
          </div>
        ))}
        {references.map((reference) => {
          const asset = assetsById.get(reference.assetId);
          const allowedRoles = modelCapability ? rolesForKind(reference.kind, modelCapability) : [];
          const isUnsupportedRole = !allowedRoles.includes(reference.role);
          return (
            <div className="studio-seedance-composer__tag" key={reference.edgeId}>
              {reference.kind === "audio" ? <span aria-hidden="true">♫</span> : <img src={assetThumbnailUrl(reference.assetId)} alt="" />}
              <strong>{reference.mention}</strong>
              <span title={asset?.displayName}>{asset?.displayName ?? reference.assetId}</span>
              <label>
                <span className="sr-only">{`${reference.mention} 的用途`}</span>
                <select
                  aria-label={`${reference.mention} 的用途`}
                  value={reference.role}
                  onChange={(event) => onChange(updateReferenceRole(document, reference.edgeId, event.currentTarget.value as ReferenceRole))}
                >
                  {isUnsupportedRole ? <option value={reference.role} disabled>{`${referenceRoleLabel(reference.role)}（当前模型不支持）`}</option> : null}
                  {allowedRoles.map((role) => <option key={role} value={role}>{referenceRoleLabel(role)}</option>)}
                </select>
              </label>
              {reference.kind === "video" ? (
                <label>
                  <span className="sr-only">{`${reference.mention} 的模式`}</span>
                  <select
                    aria-label={`${reference.mention} 的模式`}
                    value={reference.mode ?? "strict"}
                    onChange={(event) => onChange(updateReferenceMode(document, reference.edgeId, event.currentTarget.value as ReferenceMode))}
                  >
                    {videoModes.map((mode) => <option key={mode} value={mode}>{referenceModeLabel(mode)}</option>)}
                  </select>
                </label>
              ) : null}
              <button
                type="button"
                aria-label={`移除 ${reference.mention}`}
                onClick={() => createCanvasEditingDispatch({getDocument:()=>document,change:onChange}).deleteEdges([reference.edgeId])}
              >×</button>
            </div>
          );
        })}
      </div>
      <label className="studio-seedance-composer__prompt-label">
        <span>完整提示词</span>
        <div ref={editorRef}>
          <StructuredPromptEditor ariaLabel="完整提示词" value={prompt} maxLength={20_000}
            {...(onUndo ? {onUndo} : {})} {...(onRedo ? {onRedo} : {})}
            onChange={prompt => changeSettings({prompt})}
            onRequestReference={(range,query) => setQuery({nodeId:seedanceNode.id,range,query,content:structuredClone(prompt)})}
            renderReference={(reference,actions) => {
              const binding = seedanceNode.referenceBindings.find(binding => binding.slotId === reference.slotId && binding.source.nodeId === seedanceNode.id && binding.source.field === "prompt" && binding.source.inputEdgeId === null);
              const identity = binding?.value.state === "bound" ? binding.value.target : undefined;
              const label = identity ? references.find(item => item.edgeId === identity.edgeId && item.nodeId === identity.nodeId && item.assetId === identity.assetId)?.mention : undefined;
              return <PromptBindingStatus reference={reference} hasTargetContext {...(binding ? {binding} : {})} {...(label ? {targetLabel:label} : {})} onRebind={actions.onRebind} onRemove={actions.onRemove} />;
            }} />
        </div>
      </label>
      <section className="studio-seedance-composer__effective-prompt" aria-label="实际生成提示词预览">
        <strong>实际生成提示词预览</strong>
        <pre>{effectivePrompt || "（空）"}</pre>
      </section>
      <div className="studio-seedance-composer__prompt-tools">
        <button
          type="button"
          disabled={!templateContent.trim()}
          aria-expanded={templateSaveOpen}
          onClick={() => setTemplateSaveOpen((current) => !current)}
        >
          {canvasCopy.savePromptTemplate}
        </button>
        <button
          type="button"
          aria-expanded={templatePickerOpen}
          onMouseDown={() => {
            const root = editorRef.current?.querySelector<HTMLElement>("[contenteditable]");
            setTemplateSelection(openPromptTemplateSelection(document,{nodeId:seedanceNode.id,field:"prompt",inputEdgeId:null},root ? readPromptSelection(root,prompt) ?? undefined : undefined));
          }}
          onClick={() => { if (!templatePickerOpen) setTemplateSelection(openPromptTemplateSelection(document,{nodeId:seedanceNode.id,field:"prompt",inputEdgeId:null},templateSelection && JSON.stringify(templateSelection.content) === JSON.stringify(prompt) && JSON.stringify(templateSelection.source) === JSON.stringify({nodeId:seedanceNode.id,field:"prompt",inputEdgeId:null}) ? templateSelection.range : undefined)); setTemplatePickerOpen(current=>!current); }}
        >
          {canvasCopy.myPromptTemplates}
        </button>
        <button type="button" aria-label="管理模板" onClick={onManagePromptTemplates}>管理模板</button>
      </div>
      {templateSaveOpen ? (
        <div className="studio-seedance-composer__template-save">
          <input
            aria-label={canvasCopy.promptTemplateTitle}
            placeholder={canvasCopy.promptTemplateTitle}
            value={templateTitle}
            maxLength={160}
            onChange={(event) => setTemplateTitle(event.currentTarget.value)}
          />
          <button type="button" disabled={!templateTitle.trim() || !templateContent.trim()} onClick={() => void saveTemplate()}>
            {canvasCopy.confirmSavePromptTemplate}
          </button>
        </div>
      ) : null}
      <PromptTemplatePicker
        open={templatePickerOpen}
        onApply={applyTemplate}
        onClose={() => setTemplatePickerOpen(false)}
      />
      {templateStatus ? <output role="status">{templateStatus}</output> : null}
      {mentionList.open && query !== undefined ? (
        <div className="studio-seedance-composer__mentions" role="listbox" aria-label="选择引用素材" aria-activedescendant={options[mentionList.activeIndex] ? `composer-candidate-${mentionList.activeIndex}` : undefined}>
          {candidates.canvas.length > 0 ? <h3>当前画布</h3> : null}
          {candidates.canvas.map((candidate, index) => (
            <button
              id={`composer-candidate-${index}`}
              key={`canvas-${candidate.nodeId}-${candidate.edgeId}`}
              type="button"
              role="option"
              aria-selected={mentionList.activeIndex === index}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectCandidate(candidate)}
            >
              {candidate.asset.kind === "audio" ? <span aria-hidden="true">♫</span> : <img src={assetThumbnailUrl(candidate.asset.id)} alt="" />}
              <span>{candidate.asset.displayName} · 画布素材 {index + 1}</span>
            </button>
          ))}
          {candidates.library.length > 0 ? <h3>我的私有素材库</h3> : null}
          {candidates.library.map((candidate, libraryIndex) => {
            const index = candidates.canvas.length + libraryIndex;
            return (
              <button
                id={`composer-candidate-${index}`}
                key={`library-${candidate.asset.id}`}
                type="button"
                role="option"
                aria-selected={mentionList.activeIndex === index}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectCandidate(candidate)}
              >
                {candidate.asset.kind === "audio" ? <span aria-hidden="true">♫</span> : <img src={assetThumbnailUrl(candidate.asset.id)} alt="" />}
                <span>{candidate.asset.displayName}</span>
              </button>
            );
          })}
          {candidateStatus === "loading" ? <p role="status">正在搜索私有素材…</p> : null}
          {candidateHasMore ? <button type="button" onClick={onLoadMoreCandidates}>加载更多</button> : null}
        </div>
      ) : null}
      <div className="studio-seedance-composer__settings">
        <label>{canvasCopy.model}
          <select value={seedanceNode.settings.model} onChange={(event) => changeModel(event.currentTarget.value as SeedanceNodeSettings["model"])}>
            {capabilities.models.map(({ id }) => <option key={id} value={id}>{seedanceModelLabel(id)}</option>)}
          </select>
        </label>
        <label>{canvasCopy.ratio}
          <select value={seedanceNode.settings.ratio} onChange={(event) => changeSettings({ ratio: event.currentTarget.value as SeedanceNodeSettings["ratio"] })}>
            {capabilities.ratios.map((ratio) => <option key={ratio} value={ratio}>{ratio}</option>)}
          </select>
        </label>
        <label>时长（秒）
          <input type="number" value={seedanceNode.settings.durationSeconds} min={modelCapability?.durationSeconds.minimum} max={modelCapability?.durationSeconds.maximum} step={modelCapability?.durationSeconds.step} onChange={(event) => changeDuration(event.currentTarget.value)} />
        </label>
        <label>{canvasCopy.resolution}
          <select value={seedanceNode.settings.resolution} onChange={(event) => changeSettings({ resolution: event.currentTarget.value as SeedanceNodeSettings["resolution"] })}>
            {modelCapability?.resolutions.map((resolution) => <option key={resolution} value={resolution}>{resolution}</option>)}
          </select>
        </label>
        <label>{canvasCopy.seed}
          <input type="number" value={seedanceNode.settings.seed} min={capabilities.seed.minimum} max={capabilities.seed.maximum} onChange={(event) => changeSeed(event.currentTarget.value)} />
        </label>
        <label className="studio-seedance-composer__check"><input type="checkbox" checked={seedanceNode.settings.generateAudio} disabled={!modelCapability?.supportsAudio} onChange={(event) => changeSettings({ generateAudio: event.currentTarget.checked })} />{canvasCopy.generateAudio}</label>
        <label className="studio-seedance-composer__check"><input type="checkbox" checked={seedanceNode.settings.watermark} disabled={!modelCapability?.supportsWatermark} onChange={(event) => changeSettings({ watermark: event.currentTarget.checked })} />{canvasCopy.watermark}</label>
      </div>
      <footer className="studio-seedance-composer__footer">
        <p>提交前会确认画布已保存，并使用当前节点配置创建生成任务。</p>
        <button
          type="button"
          aria-label="提交 Seedance 生成"
          disabled={submitting}
          onClick={() => void submitGeneration()}
        >↑</button>
      </footer>
      {validationStatus ? <output role="status">{validationStatus}</output> : null}
    </section>
  );
}
